import { Button, ChangeEvent, ClickEvent, ComponentSearcher, PanelProps, Table, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { ArrayUtil, Collection, ModelRow } from "@mcleod/core";
import { RowPermsSecureItem } from "../models/ModelPermsSecureItem";
import { AutogenLayoutPanelPermsGrant } from "./autogen/AutogenLayoutPanelPermsGrant";
import { GroupSecureItemTooltip } from "./GroupSecureItemTooltip";
import { PanelIndividualSecure } from "./PanelIndividualSecure";
import { PanelMultiSecure } from "./PanelMultiSecure";
import { PanelSecureItem } from "./PanelSecureItem";
import { PermissionsMain } from "./PermissionsMain";
import { PermsContainer } from "./PermsContainer";
import { PermsUserAndGroupCache } from "./PermsUserAndGroupCache ";
import { SecureItem } from "./SecureItem";
import { UserOrGroup } from "./UserOrGroup";

export interface PanelPermsGrantProps extends PanelProps {
    permsContainer: PermsContainer;
}

// Represents a table of assignable users or groups for granting permissions.
export interface UserOrGroupTable {
    // Indicates whether the table is for users or groups
    userOrGroup: UserOrGroup;
    // The table displaying the assignable users or groups
    table: Table;
    // All relevant users or groups, including those that have perms granted
    // and were removed from the table
    eligibleData: Collection<ModelRow>;
}


const expandedWidth = 680;
const collapsedWidth = 328;

export class PanelPermsGrant extends AutogenLayoutPanelPermsGrant implements PanelPermsGrantProps {
    private _permsContainer: PermsContainer;
    private _selectedChild: PanelSecureItem;
    lmeUsersTable: UserOrGroupTable;
    webUsersTable: UserOrGroupTable;
    groupsTable: UserOrGroupTable;

    async onLoad() {
        this.style.transition = "width 200ms";
        this.setProps({ width: collapsedWidth, fillHeight: true, padding: 0, fillRow: false });
        this.clear();
    }

    get permsUserCache(): PermsUserAndGroupCache {
        return this.permissionsSource?.permsUserCache;
    }

    get permsContainer(): PermsContainer {
        return this._permsContainer;
    }

    set permsContainer(value: PermsContainer) {
        this._permsContainer = value;
        this.setupTables();
    }

    setupTables() {
        this.tableGroups.filterTextbox?.setProps({width: 165, placeholder: "Filter groups"});
        this.tableUsers.filterTextbox?.setProps({width: 165, placeholder: "Filter users"});
        this.tableWebUsers.filterTextbox?.setProps({width: 165, placeholder: "Filter web users"});
        const compIdSearcher = new ComponentSearcher("id");
        this.tableGroups.addlSearcherCallback = () => [compIdSearcher];
        this.tableUsers.addlSearcherCallback = () => [compIdSearcher];
        this.tableWebUsers.addlSearcherCallback = () => [compIdSearcher];

        this.lmeUsersTable = { table: this.tableUsers, userOrGroup: "U", eligibleData: this.permsUserCache.lmeUsers };
        this.webUsersTable = { table: this.tableWebUsers, userOrGroup: "U", eligibleData: this.permsUserCache.webUsers };
        this.groupsTable = { table: this.tableGroups, userOrGroup: "G", eligibleData: this.permsUserCache.userGroups };
    }

    public getRelevantUserOrGroupTable(userOrGroup: UserOrGroup, userOrGroupId: string): UserOrGroupTable {
        if (userOrGroup === "U") {
            if (userOrGroupId in this.lmeUsersTable.eligibleData) {
                return this.lmeUsersTable;
            }
            if (userOrGroupId in this.webUsersTable.eligibleData) {
                return this.webUsersTable;
            }
        } else if (userOrGroup === "G") {
            return this.groupsTable;
        }
        return null;
    }

    public clear() {
        this.setItemsBeingEdited(null);
    }

    get permissionsSource() {
        return this.permsContainer?.permissionsSource;
    }

    public setItemsBeingEdited(secureItems: SecureItem[]) {
        try {
            this.panelSecures.removeAll();
            this.labelHeading.text = "Permissions";
            const noItemsBeingEdited = ArrayUtil.isEmptyArray(secureItems);
            if (noItemsBeingEdited) {
                this.panelSecures.add(this.labelSelectItem);
                this.selectedChild = null;
                return;
            }

            if(secureItems.length > 1 && this.permsContainer instanceof PermissionsMain) {
                const panelMultiSecure = new PanelMultiSecure(secureItems, {permsGrant: this});
                this.panelSecures.add(panelMultiSecure);
            } else {
                const itemNames = new Set<string>();
                for (const secureItem of secureItems) {
                    // the only time we should have multiple secure items is when setting perms on a single component
                    // For example, a Textbox would have a view security and edit security
                    // We currently don't support setting permissions on multiple components at once so the
                    // commonItemName should always be the component.id
                    const panelIndividualSecure = new PanelIndividualSecure(secureItem, { permsGrant: this });
                    this.panelSecures.add(panelIndividualSecure);
                    itemNames.add(secureItem.itemName);
                }

                if (itemNames.size > 1) {
                    throw new Error("Please select a single item to set permissions on");
                }

                const commonItemName = itemNames.values().next().value;
                let suffix = "";
                if (commonItemName) {
                    suffix = RowPermsSecureItem.ENTIRE_SCREEN === commonItemName
                        ? "Entire Page"
                        : commonItemName;
                }

                this.labelHeading.text = `Permissions for ${suffix}`;
            }

            this.selectedChild = this.panelSecures.components[0] as PanelSecureItem;

        } catch(error) {
            this.selectedChild = null;
            this.labelError.visible = true;
            this.labelError.text = error.message;
            this.panelSecures.add(this.labelError);
            return;
        }
    }

    buttonToggleUsersOnClick(_event) {
        this.showUsersTables(!this.panelUserGroups.visible);
    }

    syncButtonToggleUsersVisibility() {
        this.buttonToggleUsers.visible = this.selectedChildPermed;
    }

    private showUsersTables(value: boolean) {
        if (this.panelUserGroups.visible === value) {
            return;
        }

        this.panelUserGroups.visible = value;
        if (value) {
            // Without this listener, the initial selected tab header wasn't underlined.
            // I assume this was because the parent of the tabset was not visible and off screen
            const handleTransitionEnd = (event: TransitionEvent) => {
                if (event.propertyName === "width") {
                    //Refresh the underline after the transition ends.
                    this.tabsetUsers.refreshUnderline();
                    this._element.removeEventListener("transitionend", handleTransitionEnd);
                }
            };

            this._element.addEventListener('transitionend', handleTransitionEnd);
            this.width = expandedWidth;
            this.buttonToggleUsers.setProps({ caption: "Hide users and groups", imageRotation: 90 });
        } else {
            this.width = collapsedWidth;
            this.buttonToggleUsers.setProps({ caption: "Add allowed users or groups", imageRotation: 270 });
        }
    }

    get selectedChild(): PanelSecureItem {
        return this._selectedChild;
    }

    set selectedChild(child: PanelSecureItem) {
        if (this._selectedChild === child)
            return;
        this.styleSelectedChild(false);
        this._selectedChild = child;
        if (this._selectedChild != null) {
            this.styleSelectedChild(true);
            this._selectedChild.syncTables();
            this._selectedChild.switchSecured.addChangeListener(this.switchSecuredOnChange);
        }
        this.syncButtonToggleUsersVisibility();
        this.showUsersTables(child?.secured ?? false);
    }

    private styleSelectedChild(selecting: boolean) {
        if (this.selectedChild)
            this.selectedChild.backgroundColor = selecting ? "primary.lightest" : "defaultBackground";
    }

    private switchSecuredOnChange = (event: ChangeEvent) => {
        this.syncButtonToggleUsersVisibility();
        if (event.newValue !== true)
            this.showUsersTables(false);
    }

    get selectedChildPermed(): boolean {
        return this.selectedChild?.switchSecured?.checked ?? false;
    }

    buttonAddUserOnClick(event: ClickEvent) {
        this.addButtonClicked(event, "U");
    }

    buttonAddGroupOnClick(event: ClickEvent) {
        this.addButtonClicked(event, "G");
    }

    async addButtonClicked(event: ClickEvent, userOrGroup: UserOrGroup) {
        if (this.selectedChild == null)
            return;

        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const table = tableRow.table;

        if (!table.selectedIndexes.includes(tableRow.index)) {
            this.addAllowedUserOrGroup(userOrGroup, tableRow, event.target.id);
        } else {
            const selectedRows = table.selectedRows;
            for (const selectedRow of selectedRows) {
                this.addAllowedUserOrGroup(userOrGroup, selectedRow, event.target.id);
            }
        }
    }

    addAllowedUserOrGroup(userOrGroup: UserOrGroup, tableRow: TableRow, buttonId: string) {
        const targetButton = tableRow.findComponentById(buttonId) as Button;
        targetButton.busy = true;
        this.selectedChild.addAllowed(userOrGroup, tableRow.data.get("id"));
        targetButton.busy = false;
    }

    /** This is an event handler for the onRowDisplay event of tableGroups. */
    tableGroupsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target;
        const label = tableRow.findComponentById("labelGroupDescr");
        if (label != null) {
            GroupSecureItemTooltip.addTooltipCallback(label, tableRow.data?.get("id"), this.permsUserCache);
        }
    }
}
